import { Button, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Tooltip } from '@mui/material';
import { Check as CheckIcon, DoNotDisturb as DoNotDisturbIcon, ContentCopy as ContentCopyIcon, Delete as DeleteIcon } from '@mui/icons-material';

import { useTrans } from "~/hooks/useTrans";
import { KEY_PREVIEW_LENGTH } from "~/const";
import { IClientKey } from "~/providers/client-interfaces";
import { IAdminKey } from "~/providers/admin-interfaces";
import Loader from '~/components/Loader';
import InternalDescription from "~/components/InternalDescription";

interface IKeysTable {
	isInternal: boolean;
	isVisitor: boolean;
	loading: boolean;
	items: Array<IClientKey | IAdminKey>;
	onCopy: (api: string) => void;
	onRemove: (keyData: IClientKey | IAdminKey) => void;
	onSettings: (keyData: IClientKey | IAdminKey) => void;
}

export default function KeysTable({
	isInternal = false,
	isVisitor = false,
	loading = true,
	items = [],
	onCopy = () => {},
	onRemove = () => {},
	onSettings = () => {},
}: IKeysTable) {
	const { trans } = useTrans();
	const HEADERS = [
		"apiKeyId",
		"createKey.userTitle",
		"createKey.userDescription",
		...isInternal ? ["createKey.internalDescription"] : [],
		"usersPage.tableState",
		"apiKey",
		"",
	];

	/* eslint-disable no-magic-numbers */
	return <TableContainer component={Paper}>
		<Table sx={{ minWidth: 650 }} aria-label="table">
			<TableHead>
				<TableRow>
					{ HEADERS.map(header => <TableCell sx={{ fontWeight: "bold" }} key={header}>
						{ header && trans(header) }
					</TableCell>) }
				</TableRow>
			</TableHead>
			<TableBody>
				{ items.map(keyData => (<TableRow key={keyData.id}>
					<TableCell>
						{ keyData.internalName }
					</TableCell>
					<TableCell>
						{ keyData.userTitle || trans("empty") }
					</TableCell>
					<TableCell>
						{ keyData.userDescription || trans("empty") }
					</TableCell>
					{ isInternal && <TableCell>
						<InternalDescription desc={"internalDescription" in keyData ? keyData.internalDescription : ""} />
					</TableCell> }
					<TableCell>
						<Tooltip title={keyData.forbiddenConsumptionReason}>
							{ keyData.canConsume
								? <CheckIcon sx={{ fill: "var(--dark-green-text-color)" }} />
								: <DoNotDisturbIcon sx={{ fill: "var(--red-text-color)" }} /> }
						</Tooltip>
					</TableCell>
					<TableCell>
						<span style={{ marginRight: "5px", background: "var(--key-background)", borderRadius: "10px", padding: "4px 10px" }}>
							{keyData.apiKey.slice(0, KEY_PREVIEW_LENGTH)}...
						</span>
					</TableCell>
					<TableCell>
						<div style={{ display: "flex", gap: "var(--margin-small)", justifyContent: "space-between", alignItems: "center" }}>
							<Button onClick={() => onCopy(keyData.apiKey)}>
								<ContentCopyIcon />
							</Button>
							<Button onClick={() => onRemove(keyData)} disabled={isVisitor}>
								<DeleteIcon />
							</Button>
							<Button onClick={() => onSettings(keyData)} variant="outlined" disabled={isVisitor}>
								{ trans("projectKeysPage.keySettings") }
							</Button>
						</div>
					</TableCell>
				</TableRow>)) }
				{ items.length === 0 && <TableRow>
					<TableCell colSpan={HEADERS.length} className="text-align-center">
						{ loading && <Loader msg={trans("loadingData")} /> }
						{ !loading && trans("emptyTable") }
					</TableCell>
				</TableRow> }
			</TableBody>
		</Table>
	</TableContainer>;
}
